import { usePerms } from 'contexts/PermsContext';
import GetWidgetData from 'data/widgetData/widgetData.js';
import type { LayoutItem, WidgetData } from 'features/dashboard/types/widgetTypes';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetWorkplaceMembersQuery } from 'services/organization/organization.api';
import { getPermissionsMeta } from 'services/perms/perms.service';
import { useGetAllRolesQuery, useGetRoleQuery } from 'services/roles/roles.api';
import { safeJSONParse } from 'utils/data/parse.utils';
import { getVisibleUsers } from 'utils/users/role.utils';
import type { Member, PermItem, Role, RoleFormData } from '../../types/Roles.types';
import { normalizePermName, parsePermName } from '../../utils/Roles.utils';

type Mode = 'create' | 'edit' | 'view';

export default function useRoleForm(options?: { mode?: Mode; roleId?: string | null }) {
  const { mode = 'view', roleId: roleIdProp } = options || {};
  const perms = usePerms();
  const [params, setParams] = useSearchParams();
  const roleIdFromParams = params.get('roleid');
  const roleId = roleIdProp ?? roleIdFromParams;

  const workplaceMembersQuery = useGetWorkplaceMembersQuery(getVisibleUsers(perms));
  const workplaceMembers: Member[] = (workplaceMembersQuery.data as Member[]) || [];
  const workplaceMembersFetching = workplaceMembersQuery.isFetching;

  const allRolesQuery = useGetAllRolesQuery(undefined);
  const allRoles: Role[] = (allRolesQuery.data as Role[]) || [];

  const roleQuery = useGetRoleQuery(roleId, { skip: !roleId });
  const roleData = roleQuery.data as Role | undefined;

  // Always refetch role and related lists when a roleId is present (viewing a role).
  // This ensures the UI always displays the most recent role data on navigation.
  useEffect(() => {
    if (!roleId) return;

    try {
      if (typeof roleQuery.refetch === 'function') roleQuery.refetch().catch(() => {});
    } catch (e) {
      console.log(e);
    }

    try {
      if (typeof workplaceMembersQuery?.refetch === 'function')
        workplaceMembersQuery.refetch().catch(() => {});
    } catch (e) {
      console.log(e);
    }

    try {
      if (typeof allRolesQuery?.refetch === 'function') allRolesQuery.refetch().catch(() => {});
    } catch (e) {
      console.log(e);
    }
  }, [roleId]);

  const [permList, setPermList] = useState<PermItem[]>([]);
  const [widgetData, setWidgetData] = useState<WidgetData>(GetWidgetData());
  const [formData, setFormData] = useState<RoleFormData>({
    roleName: '',
    members: [],
    initialMembers: [],
    permissions: [],
    layout: [],
  });
  const [membersList, setMembersList] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (workplaceMembersFetching) return;

      const permsMeta = (await getPermissionsMeta())?.data;
      const apiPerms: {
        name: string;
        description: string;
        linked: number;
        enableLinked?: number;
      }[] = Array.isArray(permsMeta?.meta) ? permsMeta.meta : [];

      const nextPermList: PermItem[] = apiPerms.map((p, index) => ({
        name: parsePermName(p.name),
        desc: parsePermName(p.description),
        linked: p.linked,
        enableLinked: p.enableLinked ?? 0,
        index,
      }));

      setPermList(nextPermList);
      setWidgetData(GetWidgetData());

      const userData = [...(workplaceMembers || [])];

      // If there's no role id (create mode or view without id), initialize defaults
      if (!roleId || !roleData) {
        setMembersList(userData.map((u) => ({ ...u, selected: false })));
        setFormData((prev) => ({ ...prev, permissions: nextPermList.map(() => false) }));
        setLoading(false);
        return;
      }

      const roleResult = roleData as Role;
      const parsedPerms = safeJSONParse(roleResult.role_perms, {});

      const permissions = nextPermList.map(() => false);
      Object.keys(parsedPerms || {}).forEach((key) => {
        const str = parsePermName(key);
        const index = nextPermList.findIndex((item) => item.name === str);
        if (index >= 0) permissions[index] = Boolean(parsedPerms[key]);
      });

      const initUsers: Member[] = [];
      const temp: Member[] = [];

      for (let i = 0; i < userData.length; i++) {
        const userRoles = safeJSONParse(userData[i]?.roles, []);
        if (Array.isArray(userRoles) && userRoles.includes(parseInt(String(roleId)))) {
          initUsers.push({ ...userData[i] });
          temp.push({ ...userData[i], selected: true });
        } else {
          temp.push({ ...userData[i] });
        }
      }

      const currentRoleFromAllRoles = allRoles.find(
        (role: Role) => String(role.role_Id) === String(roleId),
      );
      const allRoleUserEmails = Array.isArray(currentRoleFromAllRoles?.users)
        ? currentRoleFromAllRoles.users
        : safeJSONParse(currentRoleFromAllRoles?.users, []);

      const accessibleEmailSet = new Set(userData.map((member) => member.email));
      (allRoleUserEmails as string[]).forEach((userEmail) => {
        if (!accessibleEmailSet.has(userEmail)) {
          const inaccessibleEntry: Member = {
            email: String(userEmail),
            selected: true,
            canModify: false,
          };
          temp.push(inaccessibleEntry);
          initUsers.push(inaccessibleEntry);
        }
      });

      setMembersList([...temp]);
      setFormData((prev) => ({
        ...prev,
        roleName: roleResult.role_name || '',
        permissions: [...permissions],
        initialMembers: [...initUsers],
        layout: safeJSONParse(roleResult.widget_list, []) as LayoutItem[],
      }));

      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workplaceMembersFetching, workplaceMembers, allRoles, roleData, roleId]);

  const getCurrentTabIndex = (tabOptions: string[]) => {
    const tabParam = params.get('tab');
    const matchedIndex = tabOptions.findIndex(
      (option) => option.toLowerCase() === tabParam?.toLowerCase(),
    );
    return matchedIndex === -1 ? 0 : matchedIndex;
  };

  const updateTabParam = (nextIndex: number, tabOptions: string[], replace = false) => {
    const newParams = new URLSearchParams(params.toString());
    newParams.set('tab', tabOptions[nextIndex]);
    setParams(newParams, { replace });
  };

  const buildPermissionsRecord = (permissionsArray?: boolean[]) => {
    const arr = Array.isArray(permissionsArray) ? permissionsArray : formData.permissions || [];
    return arr.reduce((acc: Record<string, boolean>, val: boolean, index: number) => {
      const permName = normalizePermName(permList[index]?.name || '');
      if (permName) acc[permName] = Boolean(val);
      return acc;
    }, {});
  };

  return {
    permList,
    widgetData,
    formData,
    setFormData,
    membersList,
    setMembersList,
    loading,
    getCurrentTabIndex,
    updateTabParam,
    buildPermissionsRecord,
    roleId,
    roleData,
    roleRefetch: roleQuery.refetch,
  } as const;
}
